const DEFAULT_ERROR_MESSAGES = {
    400: 'The request contains invalid data.',
    401: 'Please sign in and try again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested item was not found.',
    409: 'This item already exists or has been changed.',
    413: 'The file is too large or there is not enough storage space.',
    429: 'Too many requests. Please try again shortly.',
    500: 'The server encountered an error. Please try again later.',
    502: 'The service is temporarily unavailable. Please try again later.',
    503: 'The service is temporarily unavailable. Please try again later.',
    504: 'The request timed out. Please try again.'
}

function getReadableErrorMessage(payload, fallback) {
    if (typeof payload === 'string') {
        const text = payload.trim()
        if (!text) return fallback

        try {
            return getReadableErrorMessage(JSON.parse(text), fallback)
        } catch {
            return text
        }
    }

    if (Array.isArray(payload)) {
        const messages = payload
            .map((item) => getReadableErrorMessage(item, ''))
            .filter(Boolean)

        return messages.join(' ') || fallback
    }

    if (payload && typeof payload === 'object') {
        for (const key of ['error', 'message', 'detail', 'description', 'reason']) {
            if (payload[key] !== undefined && payload[key] !== null) {
                const message = getReadableErrorMessage(payload[key], '')
                if (message) return message
            }
        }

        if (payload.errors !== undefined) {
            return getReadableErrorMessage(payload.errors, fallback)
        }
    }

    return fallback
}

function getResponseErrorMessage(response, payload) {
    const fallback = DEFAULT_ERROR_MESSAGES[response.status]
        || response.statusText
        || `Request failed (${response.status}).`

    return getReadableErrorMessage(payload, fallback)
}

export class DemopartyApiError extends Error {
    constructor(message, { status = null, statusText = '', payload = null, method = '', path = '' } = {}) {
        super(message)
        this.name = 'DemopartyApiError'
        this.status = status
        this.statusText = statusText
        this.payload = payload
        this.method = method
        this.path = path
        this.userMessage = message
    }
}

class DemopartyClient {
    static getReadableErrorMessage(payload, fallback = 'Something went wrong. Please try again.') {
        return getReadableErrorMessage(payload, fallback)
    }

    constructor(baseURL, token = null, userId = null, username = null) {
        if (!baseURL) {
            throw new Error('DemopartyClient: baseURL is required.');
        }
        this.baseURL = baseURL;
        this.token = token;
        this.userId = userId;
        this.username = username;
    }

    setToken(token) {
        this.token = token;
    }

    setUserId(id){
        this.userId = id;
    }

    setUsername(username){
        this.username = username;
    }

    setEmail(email){
        this.email = email
    }

    _getHeaders(contentType = 'application/json', requireAuth = false) {
        const headers = {};
        if (contentType) {
            headers['Content-Type'] = contentType;
        }
        if (requireAuth && this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        } else if (requireAuth && !this.token) {
            throw new DemopartyApiError('Authentication required but no token provided.', { status: 401 });
        }
        return headers;
    }

    async _request(method, path, data = null, requireAuth = false, contentType = 'application/json') {
        const url = path.startsWith('http://') || path.startsWith('https://')
            ? path
            : `${this.baseURL}${path}`;
        const options = {
            method,
            headers: this._getHeaders(contentType, requireAuth),
        };

        if (data) {
            if (contentType === 'application/json') {
                options.body = JSON.stringify(data);
            } else {
                options.body = data; // For file uploads or other non-JSON bodies
            }
        }

        try {
            const response = await fetch(url, options);
            if (!response.ok) {
                const errorBody = await response.text().catch(() => '')
                throw new DemopartyApiError(getResponseErrorMessage(response, errorBody), {
                    status: response.status,
                    statusText: response.statusText,
                    payload: errorBody,
                    method,
                    path
                })
            }
            // Some successful responses might not have a body (e.g., DELETE)
            if (response.status === 204 || response.headers.get('Content-Length') === '0') {
                return null;
            }
            return await response.json();
        } catch (error) {
            console.log('DemopartyClient Request Error:', error);
            throw error;
        }
    }

    // --- Auth Endpoints ---
    async login(email, password) {
        const response = await this._request('POST', '/auth', { email, password });
        const token = response?.newToken ?? response?.token ?? response?.user?.token ?? null
        if (token) {
            this.setToken(token);
        }
        if (response?.user?.id) {
            this.setUserId(response.user.id);
        }
        if (response?.user?.username) {
            this.setUsername(response.user.username);
        }
        return response;
    }

    async requestMagicLink(email) {
        return this._request('POST', '/auth/magic-request', { email });
    }

    async consumeMagicLink(token) {
        const response = await this._request('POST', '/auth/magic-consume', { token });
        const sessionToken = response?.newToken ?? response?.token ?? response?.user?.token ?? null
        if (sessionToken) {
            this.setToken(sessionToken);
        }
        if (response?.user?.id) {
            this.setUserId(response.user.id);
        }
        if (response?.user?.username) {
            this.setUsername(response.user.username);
        }
        return response;
    }

    // --- User Endpoints ---
    async createUser(email, password) {
        return this._request('POST', '/user', { email, password });
    }

    async getUserProfile(userId, { includeTracks = true } = {}) {
        const query = includeTracks ? '' : '?include_tracks=false';
        return this._request('GET', `/user/${userId}${query}`, null, true);
    }

    async updateUser(userId, userData) {
        return this._request('PUT', `/user/${userId}`, userData, true);
    }

    async updateSettings(settings) {
        return this._request('PUT', '/user/me/settings', { settings }, true);
    }

    async deleteUser(userId) {
        return this._request('DELETE', `/user/${userId}`, null, true);
    }

    // --- Track Endpoints ---
    async getStandaloneTracks({ limit = 30, cursor = null } = {}) {
        const params = new URLSearchParams({ limit: String(limit) });
        if (cursor) params.set('cursor', cursor);
        return this._request('GET', `/tracks?${params.toString()}`, null, true);
    }

    async createTrack(trackData) {
        return this._request('POST', '/track', trackData, true);
    }

    async getTrack(trackId) {
        return this._request('GET', `/track/${trackId}`, null, true);
    }

    async updateTrack(trackId, trackData) {
        return this._request('PUT', `/track/${trackId}`, trackData, true);
    }

    async deleteTrack(trackId) {
        return this._request('DELETE', `/track/${trackId}`, null, true);
    }

    // --- Search Endpoints ---
    async search(query, limit = 5) {
        const params = new URLSearchParams();
        if (query) params.set('query', query);
        if (limit) params.set('limit', limit);
        const suffix = params.toString();
        return this._request('GET', `/search${suffix ? `?${suffix}` : ''}`, null, true);
    }

    // --- Project Endpoints ---
    async createProject(projectData) {
        return this._request('POST', '/project', projectData, true);
    }

    async getProject(projectId) {
        return this._request('GET', `/project/${projectId}`, null, true);
    }

    async updateProject(projectId, projectData) {
        return this._request('PUT', `/project/${projectId}`, projectData, true);
    }

    async moveProjectTrack(sourceProjectId, targetProjectId, trackId) {
        return this._request('POST', `/project/${sourceProjectId}/move-track`, { targetProjectId, trackId }, true);
    }

    async deleteProject(projectId) {
        return this._request('DELETE', `/project/${projectId}`, null, true);
    }

    async reorderProjects(order) {
        return this._request('POST', '/project/reorder', { order }, true);
    }

    async reorderProject(projectId, order) {
        return this._request('POST', `/project/${projectId}/reorder`, { order }, true);
    }

    // --- File Endpoints ---
    async uploadFilePresigned(fileId = null, file, filename = null) {
        // Upload file using signed URL
        // file: File or Blob object (required)
        // fileId: optional file ID, if not provided server will generate one
        // filename: optional filename, defaults to file.name

        if (!file) {
            throw new Error('File is required for upload.');
        }

        const actualFilename = filename || file.name || 'unnamed_file';
        const size = file.size;

        try {
            // Step 1: Request signed URL from server
            const requestBody = {
                filename: actualFilename,
                size: size
            };

            if (fileId) {
                requestBody.fileId = fileId;
            }

            const signedResponse = await this._request(
                'POST',
                `/file/${fileId || ''}`,
                requestBody,
                true
            );

            if (!signedResponse.upload_url) {
                throw new Error('Invalid response: missing upload_url');
            }

            // Step 2: Upload file directly to R2 using signed URL
            const mimeType = file.type || 'application/octet-stream';
            const uploadResponse = await fetch(signedResponse.upload_url, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': mimeType
                }
            });

            if (!uploadResponse.ok) {
                throw new DemopartyApiError(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`, {
                    status: uploadResponse.status,
                    statusText: uploadResponse.statusText,
                    method: 'PUT',
                    path: signedResponse.upload_url
                });
            }

            const completedResponse = await this._request(
                'POST',
                `/file/${signedResponse.id}/complete`,
                {
                    filename: actualFilename,
                    size
                },
                true
            );

            // Return the file metadata including the direct link
            return {
                id: completedResponse.id,
                filename: completedResponse.filename,
                size: completedResponse.size,
                link: completedResponse.link,
                message: 'File uploaded successfully'
            };

        } catch (error) {
            console.error('DemopartyClient uploadFilePresigned Error:', error);
            throw error;
        }
    }

    async getFile(file) {
        try {
            const fileInfo = await this.getFileUrl(file);
            if (!fileInfo?.link) {
                throw new Error('File URL is missing.');
            }
            const response = await fetch(fileInfo.link);
            if (!response.ok) {
                const errorBody = await response.text().catch(() => '')
                throw new DemopartyApiError(getResponseErrorMessage(response, errorBody), {
                    status: response.status,
                    statusText: response.statusText,
                    payload: errorBody,
                    method: 'GET',
                    path: fileInfo.link
                })
            }
            return await response.arrayBuffer(); // Return binary data
        } catch (error) {
            console.error('DemopartyClient getFile Error:', error);
            throw error;
        }
    }

    async getFileUrl(fileId) {
        // Get signed URL for a file by its ID
        // Returns: { id, filename, size, created_at, link }
        // Works with or without auth - public links don't require token
        return this._request('GET', `/file/${fileId}`, null, !!this.token);
    }

    async storageInfo() {
        return this._request('GET', '/storage', null, true);
    }

    async createLink(type, objectId) {
        const payload = {
            type: type,
            objectId: objectId
        }
        return this._request('POST', '/link', payload, true);
    }

    async deleteLink(linkId) {
        return this._request('DELETE', `/link/${linkId}`, null, true);
    }

    async getLink(linkId) {
        return this._request('GET', `/link/${linkId}`, null, false);
    }

    async createSubscriptionCheckout(plan) {
        return this._request('POST', '/subscription/checkout', { plan }, true);
    }

    async getBillingPortal(){
        return this._request('GET', `/subscription/portal`, null, true);
    }

    // --- Agent Endpoints ---
    async createAgent(id, name, version) {
        return this._request('POST', '/agent', { id, name, version }, true);
    }

    async deleteAgent(agentId) {
        return this._request('DELETE', `/agent/${agentId}`, null, true);
    }
}

export default DemopartyClient

// For use in environments that support ES modules or browser global
if (typeof module !== 'undefined' && module.exports) {
    module.exports = DemopartyClient;
} else if (typeof window !== 'undefined') {
    window.DemopartyClient = DemopartyClient;
}
